import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ComponentType,
} from "react";

import { useDialogs } from "@/components/shell/Dialogs";
import { useMenu } from "@/components/shell/Menu";
import { hasSelectionIn, shouldOpenContextMenu } from "@/lib/contextMenu";
import { TopBar, TopBarAction, TopBarDivider } from "@/components/shell/TopBar";
import { BottomSheet } from "@/components/shell/BottomSheet";
import { SplitPane } from "@/components/shell/SplitPane";

import { type ParsedToken } from "@/lib/search";
import { ResultList } from "@/components/dictionary/ResultList";
import { dictionaryAction } from "@/components/dictionary/dictionaryKeys";
import { KIND_LABEL, countsLabel, rowTerm } from "@/components/dictionary/resultKind";
import { useLookup, type MineSource } from "@/components/dictionary/useLookup";
import { clock } from "@/lib/dates";
import { isTextEntry } from "@/lib/dom";
import type { OverlayApi } from "@/lib/navBridge";
import { fileToBase64, storeMedia, type DeckCounts } from "@/lib/note";
import { fetchContext } from "@/lib/translate";

import { AddSentence } from "./AddSentence";
import { ComposeRow } from "./ComposeRow";

/**
 * Read's photo capture (日本-711), loaded when 撮 is first pressed. It and the OCR worker behind it
 * are a phone-only, trip-only flow, so neither is in the bundle every Read tab downloads.
 */
type PhotoCaptureProps = ComponentProps<typeof import("./photo/PhotoCapture").PhotoCapture>;
function loadPhotoCapture() {
  return lazy<ComponentType<PhotoCaptureProps>>(() =>
    import("./photo/PhotoCapture")
      .then((m) => ({ default: m.PhotoCapture }))
      // A chunk that fails to load — no signal on the trip this flow exists for, or a stale hash
      // after a deploy — would otherwise throw out of the Suspense below with no error boundary
      // anywhere above it, and unmount the whole app. Close the layer instead.
      .catch(() => ({ default: PhotoCaptureUnavailable })),
  );
}
let PhotoCapture = loadPhotoCapture();
/** Stands in for a capture layer whose chunk did not load: closes it, and re-arms the loader —
 * `lazy` caches whatever it resolved to, so without a fresh one the next 撮 could never retry. */
function PhotoCaptureUnavailable({ onClose }: PhotoCaptureProps) {
  useEffect(() => {
    PhotoCapture = loadPhotoCapture();
    onClose();
  }, [onClose]);
  return null;
}
import { FormStrip } from "./FormStrip";
import { ImageSlots, SLOT_DRAG_TYPE, type SlotKey } from "./ImageSlots";
import { isOnCard } from "./onCard";
import { RadicalPicker } from "./RadicalPicker";
import { Sentence } from "./Sentence";
import {
  firstImage,
  mediaFilename,
  parseImageFilename,
  slotForPaste,
  suffixForSlot,
} from "./mediaSlots";
import { planSlotMove, type SlotRef } from "./slotDrag";
import { nextWord, tokenAt } from "./tokens";
import { insertAtCaret, rememberRadical } from "./radicalPalette";
import { useShiftHeld } from "./useShiftHeld";
import { ContextPanel } from "./ContextPanel";
import { Library } from "./Library";
import { AskPanel } from "./AskPanel";
import { SentenceMenu, sentenceMenuItems } from "./SentenceMenu";
import { Translation } from "./Translation";
import { useAsk } from "./useAsk";
import type { AgentAction } from "./useAgentBridge";
import { FindBand } from "./FindBand";
import type { Hit } from "./logFind";
import { SENTENCE_DRAG_TYPE, TabStrip } from "./TabStrip";
import { useLogFind } from "./useLogFind";
import { logLabel } from "./logTabs";
import {
  ESTIMATED_ROW_PITCH,
  OVERSCAN,
  heightsFor,
  heightsWith,
  parseTargets,
  scrollToRow,
  visibleRange,
  type RowHeights,
} from "./logWindow";
import type { Reading } from "./useReading";
import { termsInLog, type LogEntry } from "./useReadingLog";

/** Frame `4a`'s dock height: what the 596px palette needs to lay out flat, strokes 1 through 17. */
const PICKER_DOCK_SIZE = 846;

/**
 * The sentence being edited, and where its caret is.
 *
 * The caret is state rather than something read off the DOM on demand because the radical picker
 * takes focus away from the field: by the time you pick a kanji the input has no selection left to
 * read, and the whole feature is "insert at *that* caret".
 */
interface EditState {
  entryId: string;
  text: string;
  caret: number;
}

export interface ReadProps {
  /**
   * The reading library, owned by `App`.
   *
   * Held above this component on purpose: a rail click unmounts `Read`, and the open tabs and
   * loaded sentences would go with it. It is also what lets a capture arriving over the agent
   * bridge land while you are on another surface.
   */
  log: Reading;
  /** `6c`: the split becomes a bottom sheet, and a compose row replaces the tray agent. */
  isPhone?: boolean;
  /** The tab bar, rendered below the sheet so the sheet never covers it. */
  footer?: React.ReactNode;
  /**
   * The deck counters, fetched by `App` — see `useDeckCounts`.
   *
   * Held above this component for the same reason as `log`, plus one of its own: `App` asked for
   * the identical payload to feed the rail's due badge, so a fetch here made every focus and every
   * collection change issue the same request twice against a single collection worker thread.
   *
   * Null while the hub cannot be reached, and the top bar omits the counters rather than claiming
   * zero — a confident `0 added` after a morning's mining is worse than no answer.
   */
  counts: DeckCounts | null;
  /**
   * Open a DoJG entry in 文法, by its id (日本-519) — what `開く` and `O` do on a 文法 row.
   *
   * Supplied by `App` rather than done here, because it is a navigation and `App` owns `NavState`.
   * Read is the only caller: 検索 answers about vocabulary alone now, so no grammar row can appear
   * there to be opened.
   */
  onOpenGrammar: (entryId: string) => void;
  /**
   * Move both counters by one, now, without waiting for a round trip.
   *
   * The generation guard lives with the fetch, so this also discards any answer already in flight:
   * a response issued before the add would otherwise land late and erase the bump.
   */
  onAdjustCounts: (delta: number) => void;
  /**
   * Which of Read's two faces is showing — **owned by `App`, and in the URL**.
   *
   * The library is a SUB-VIEW of Read, not a rail destination (the rail stays on 読), but it
   * replaces the whole surface and is somewhere you can be. So it is navigation: Back leaves it,
   * and a refresh inside it returns to it rather than dropping you into the log.
   */
  view: "log" | "library";
  /**
   * A sentence waiting for a destination picked in the library (`Move to log ▸ Choose from
   * library…`). Travels with `view` because the menu that started it unmounts the moment the
   * library opens — and is deliberately never serialized, so a refresh forgets the intent rather
   * than restoring a half-finished move.
   */
  moving: string | null;
  onOpenLibrary: (moving?: string | null) => void;
  onCloseLibrary: () => void;
  /** Layers that Back should close — see `navHistory.ts`. Used for the radical picker. */
  overlay: OverlayApi;
  /**
   * The latest global Read shortcut the tray agent relayed (日本-710) — a mouse button doing `A` or
   * `T` while the visual novel has the focus. Acted on when its nonce changes, never at mount.
   */
  agentAction?: AgentAction | null;
}

export function Read({
  log,
  isPhone = false,
  footer,
  counts,
  onAdjustCounts,
  onOpenGrammar,
  view,
  moving: movingSentence,
  onOpenLibrary,
  onCloseLibrary,
  overlay,
  agentAction = null,
}: ReadProps) {
  const { entries, focusedId } = log;
  const libraryOpen = view === "library";

  /**
   * The pivot: a sentence and a CHARACTER OFFSET into it — not a token index (日本-512).
   *
   * `Selection` documents why. Everything that wants the token asks `tokenAt`.
   */
  const [selected, setSelected] = useState<{ entryId: string; index: number } | null>(null);
  /**
   * Which FORM-strip chip the dock is answering, if any (日本-512, gap B).
   *
   * Separate from `selected` because it is a different KIND of question: `selected` points at a
   * run of the sentence, a chip points at a dictionary form that is nowhere in the sentence —
   * しまう is not in 食べてしまった, it is what MeCab resolved しまっ to. So it cannot be expressed
   * as an offset, and folding it in would mean an offset that does not index the text.
   *
   * It is cleared by any sentence hover, which is what keeps the dock single-tenant with no mode:
   * the two ways to ask a question take it from each other rather than coexisting.
   */
  const [chipRoot, setChipRoot] = useState<{ entryId: string; root: string } | null>(null);
  const [canTranslate, setCanTranslate] = useState(false);
  /** Which sentence's `context ▾` is open, if any. Null when the panel is closed. */
  const [contextFor, setContextFor] = useState<string | null>(null);
  /**
   * The row whose `context ▾` is open, so the panel can tell its own trigger from an outside click.
   *
   * Without it that button is a one-way switch: the panel's `mousedown` closer fires first and
   * clears `contextFor`, and the `click` that follows toggles it straight back on. Attached only to
   * the open row, so one ref serves a list — every other row would otherwise overwrite it.
   */
  const contextAnchor = useRef<HTMLButtonElement>(null);
  /**
   * Ask — the dock's second segment (日本-325).
   *
   * Handed `applyTranslation` rather than `setTranslation`: the agent's `set_translation` tool has
   * already written the field, so what is left is the repaint. A second PATCH would race any
   * correction the reader had begun in the meantime.
   */
  const ask = useAsk(log.activeLogId, log.applyTranslation);
  /**
   * Whether Ask currently holds the dock, as a ref.
   *
   * `selectPivot` has to be stable for the whole life of the surface or memoising `Sentence` buys
   * nothing (see below), and it cannot be stable while it closes over `ask.mode`. The guard is a
   * read at hover time, so a ref answers it exactly and costs no identity.
   */
  const askModeRef = useRef(false);
  askModeRef.current = ask.mode === "ask" && ask.available;
  const [editing, setEditing] = useState<EditState | null>(null);
  const [pickerOpen, setPickerOpenState] = useState(false);
  const dialogs = useDialogs();
  const menu = useMenu();
  /**
   * The radical picker is a **layer**: it takes over the whole dock, so Back closes it exactly as
   * Escape does. Routing every open and close through one setter is what keeps that safe — the
   * picker is closed from seven places (Escape, the toggle, committing or cancelling an edit,
   * inserting a kanji, deleting the entry, the compose row), and one that forgot to consume its
   * history entry would leave a Back that appears to do nothing.
   */
  const setPickerOpen = useCallback(
    (open: boolean) => {
      setPickerOpenState(open);
      if (open) overlay.openOverlay("picker");
      else overlay.closeOverlay("picker");
    },
    [overlay],
  );
  // Closing it *because* of a Back: the entry is already gone, so this must not consume another.
  useEffect(
    () => overlay.registerOverlay("picker", () => setPickerOpenState(false)),
    [overlay],
  );
  /**
   * The photo capture (日本-711) is a layer too, on the picker's terms: one setter for every open
   * and close, and a registered close for Back, which has already consumed the entry.
   */
  const [photoFile, setPhotoFileState] = useState<File | null>(null);
  const setPhotoFile = useCallback(
    (file: File | null) => {
      setPhotoFileState(file);
      if (file) overlay.openOverlay("photo");
      else overlay.closeOverlay("photo");
    },
    [overlay],
  );
  useEffect(
    () => overlay.registerOverlay("photo", () => setPhotoFileState(null)),
    [overlay],
  );
  // Kept for the session, not per opening: "the next character you can't type often shares one".
  const [radicals, setRadicals] = useState<string[]>([]);
  const [recentRadicals, setRecentRadicals] = useState<string[]>([]);
  const [uploading, setUploading] = useState<{ entryId: string; which: SlotKey } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  // ------------------------------------------------------------------ windowing the log (日本-602)
  //
  // Alex's own log is 1,937 sentences and every one of them used to render: 128,240 DOM elements
  // and ~1.2-1.4 GB of browser memory, 8.5 s to open. `Sentence` draws a span per CHARACTER since
  // 日本-512, so a row costs far more than a line of text looks. `logWindow.ts` holds the
  // arithmetic, pure and tested; what lives here is the measuring and the plumbing.
  //
  // The scroller is the SHELL's (`SplitPane` on desktop, `BottomSheet` on a phone), which is why
  // both of them now take a `mainRef`.
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [scroll, setScroll] = useState({ top: 0, height: 0 });
  /** Measured pitches, by sentence id. Kept for the session — see `logWindow.RowHeights`. */
  const [rowHeights, setRowHeights] = useState<RowHeights>(() => new Map());
  const rowObserver = useRef<ResizeObserver | null>(null);
  const editRef = useRef<HTMLInputElement>(null);
  /** Clipboard events carry no modifier state, so Shift is tracked off the keyboard instead. */
  const shiftHeld = useShiftHeld();

  // Both capture arrival paths — the `/?q=` deep link and the agent's SSE bridge — live in `App`
  // now, because they have to work while this component is unmounted.

  // The counters used to be fetched here, on focus and on any collection change. `App` asked for
  // the identical payload to feed the rail's due badge, so both fired on every trigger and the
  // second one queued behind the first on the collection's single worker thread. The fetch, its
  // refresh triggers and the generation guard all moved to `useDeckCounts`; what arrives here is
  // `counts` and a way to move it. `new` is still the UNCAPPED backlog — the `· 23d` runway divides
  // it by the new-per-day limit, so a capped figure would make the runway say "one day, forever".

  /**
   * The dock's results, and everything that follows from having looked a word up.
   *
   * Extracted to `components/dictionary/useLookup.ts` in 日本-497 so 検索 is the same dock driven by
   * a text field instead of a hovered token. Every rule that used to live here moved with its
   * comment; what stays in this file is what is genuinely Read's — the pivot that produces the
   * query, the FORM strip, the picker, and the per-selection teardown below.
   */
  const lookupQuery = useMemo(() => {
    // **A chip wins, and it is asked as a typed query.** Its root is a whole word rather than a
    // position in the sentence, so it goes in the shape 検索 uses — `index: 0` with no anchor,
    // meaning *the whole text is the word*. That is also what makes 〜てしまう rank above the
    // 仕舞う fan-out: `vocab_rank` measures against the query, and the query is しまう exactly.
    if (chipRoot) return { text: chipRoot.root, index: 0, term: undefined };
    if (!selected) return null;
    const entry = entries.find((e) => e.id === selected.entryId);
    if (!entry) return null;
    const found = tokenAt(entry.tokens, selected.index);
    if (!found) return null;
    const { token } = found;
    /**
     * The anchor — what a dictionary row's exactness is measured against, and what the grammar
     * dictionary's window starts from (日本-508). See `LookupQuery.term`.
     *
     * At a token START this is the token, exactly as before: 日本-508's rule is untouched, and
     * every existing behaviour with it.
     *
     * At an INTERIOR offset it is the rest of the token from the pivot — "from here to the end of
     * the word I am inside". The alternative is circular: the honest anchor would be *the word the
     * reader is pointing at*, which is what the lookup is being asked to find out. Rest-of-token
     * degrades correctly rather than merely safely: at the 道 of 弓道 the anchor is 道, so every
     * row ranks EXACT and the ordering falls back to `/lookup`'s own — which is longest match
     * first, and therefore puts 道場 above 道. That is the order this feature wants.
     */
    const offsetInToken = selected.index - token.start;
    const anchor = offsetInToken === 0 ? token.surface : [...token.surface].slice(offsetInToken).join("");
    return { text: entry.text, index: selected.index, term: anchor };
  }, [chipRoot, selected, entries]);

  /**
   * The log's own terms, which the underlines are drawn from — the dock's results are added to
   * these by the hook. Memoised because it joins the memo that decides what to ask the collection
   * about, and a fresh array every render would re-ask on every hover.
   */
  const logTerms = useMemo(() => termsInLog(entries), [entries]);

  /**
   * The hover pivot, as ONE stable callback for the whole log.
   *
   * A closure per row gave every `Sentence` a new prop on every render, which made memoising them
   * useless — and since 日本-512 each one renders a span per character, so re-rendering the whole
   * log on every dock answer was measured at 175ms between the answer arriving and the highlight
   * painting. Taking the entry id as an argument is what lets this be defined once.
   *
   * `ask` is read through a ref-free dependency because the guard is cheap and the identity of
   * this callback is what matters; `setChipRoot` and `setSelected` are setters and never change.
   */
  const selectPivot = useCallback(
    (entryId: string, index: number) => {
      // **Suspended while Ask holds the dock.** The dock is single-tenant, so a lookup fired here
      // has nowhere to render — and switching the segment back on hover would eat a half-typed
      // question. Pointing at a sentence is the gutter's `?` and `Ctrl+/` in that mode.
      if (askModeRef.current) return;
      // A sentence hover takes the dock back from a chip — see `chipRoot`.
      setChipRoot(null);
      setSelected({ entryId, index });
    },
    [],
  );

  /**
   * **すべて / 語彙 / 文法** (Alex, 30 Aug 2026). Read keeps both kinds — 検索 is the surface
   * grammar left — so this is where they need telling apart.
   *
   * Measured on しまう: 語彙69件 · 文法1件, and the DoJG row sits at #6 of 70, y=1494px — below the
   * fold in a 1440px window. 文法 makes it the only row.
   *
   * **Per-device churn, deliberately not in `NavState`** (日本-321's rule): it is a lens on the
   * list you are already looking at, not a place, so a history entry per press would bury the one
   * that leaves the app.
   */
  const [dockKind, setDockKind] = useState<"all" | "vocab" | "grammar">("all");
  const lookup = useLookup({
    query: lookupQuery,
    extraTerms: logTerms,
    onAdjustCounts,
    // Applied inside the hook so `focusedResult`, `busyRow` and `expanded.row` index what is drawn.
    kind: dockKind,
  });
  const { rows: dockRows, allRows: allDockRows, carded, expanded, focusedResult } = lookup;

  /**
   * **Which sentence the dock's `match` was answered for** (日本-729). `match` is an offset and a
   * length and nothing else, so the moment the selection moved to another row that row drew the
   * PREVIOUS answer's span at the same offset — a run that is not a word there — until its own
   * answer landed. Hovering across rows always did it for a round trip; a sentence move, which now
   * pivots on every press, made it the normal case. Captured when the match changes, which is when
   * the answer for the current selection arrives; the old match is simply not drawn meanwhile.
   */
  const [matchOwner, setMatchOwner] = useState<{
    match: typeof lookup.match;
    entryId: string | null;
  }>({ match: null, entryId: null });
  if (lookup.match !== matchOwner.match) {
    setMatchOwner({ match: lookup.match, entryId: selected?.entryId ?? null });
  }
  // The counts describe the WHOLE answer, never the filtered view — a count that shrank with the
  // filter could not tell you the grammar dictionary had answered, which is the one thing it is for.
  const dockKinds = useMemo(() => countsLabel(allDockRows), [allDockRows]);
  // Kept under their old names: ~15 call sites in this file set a dock message, and the hook owning
  // the state does not make any of them a different act.
  const dockError = lookup.error;
  const setDockError = lookup.setError;


  /**
   * Track the scroller's position and height.
   *
   * rAF-throttled, because a scroll fires far faster than a frame and each one re-renders the
   * window. Measured with `clientHeight` rather than `innerHeight`: the scroller is 785px inside a
   * 1440px window on desktop and 413px inside 844px on a phone, so the viewport is not the screen.
   *
   * The `resize` half is not optional. The dock grip changes the scroller's height without any
   * scroll happening at all, so a window sized once at mount goes short — and a short window is a
   * blank strip at the bottom of the log rather than anything that looks like a bug.
   */
  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      setScroll((current) =>
        current.top === node.scrollTop && current.height === node.clientHeight
          ? current
          : { top: node.scrollTop, height: node.clientHeight },
      );
    };
    const onScroll = () => {
      if (frame === 0) frame = requestAnimationFrame(read);
    };
    read();
    node.addEventListener("scroll", onScroll, { passive: true });
    const observer = new ResizeObserver(onScroll);
    observer.observe(node);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      node.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
    // `libraryOpen` swaps the whole region, so the scroller node is a different element after it.
  }, [libraryOpen, isPhone]);

  /**
   * One `ResizeObserver` for every row in the window, feeding `rowHeights`.
   *
   * One observer rather than one per row: a row is mounted and unmounted constantly as the window
   * moves, and an observer each would be 1,937 of them over a scroll through the log.
   *
   * The pitch stored is the row's height **plus the gap under it** — see `RowHeights`. The gap is
   * read off the rendered element rather than hardcoded, so the two branches of `logRegion`'s own
   * className (22px desktop, 18px phone) cannot drift from this. It is read once per PARENT per
   * batch rather than once per row: every row in a window shares one parent, and a
   * `getComputedStyle` per entry is a style recalculation per row inside the observer callback.
   *
   * **Built lazily rather than in an effect, and that is not a style choice.** A ref callback runs
   * during the commit, which is *before* passive effects — so an observer created in a
   * `useEffect([])` does not exist yet when the first window's rows attach their refs, and those
   * rows are then never observed at all. Read remounts with its entries already loaded on every
   * rail switch back to 読書 (`App.renderSurface` branches on the destination), so that first
   * window is the ordinary case rather than the cold-start one, and it fails silently: the rows on
   * screen simply keep the estimate for the life of that window.
   */
  const rowObserverFor = useCallback(() => {
    if (rowObserver.current) return rowObserver.current;
    const observer = new ResizeObserver((seen) => {
      setRowHeights((current) => {
        let next = current;
        const gaps = new Map<Element, number>();
        for (const item of seen) {
          const id = (item.target as HTMLElement).dataset.sentenceId;
          if (!id) continue;
          const parent = item.target.parentElement ?? item.target;
          let gap = gaps.get(parent);
          if (gap === undefined) {
            const measured = Number.parseFloat(getComputedStyle(parent).rowGap || "0");
            gap = Number.isFinite(measured) ? measured : 0;
            gaps.set(parent, gap);
          }
          next = heightsWith(next, id, item.target.getBoundingClientRect().height + gap);
        }
        return next;
      });
    });
    rowObserver.current = observer;
    return observer;
  }, []);

  useEffect(
    () => () => {
      rowObserver.current?.disconnect();
      rowObserver.current = null;
    },
    [],
  );

  /**
   * Measure a rendered row. React 19 ref callbacks may return a cleanup, which is what lets one
   * observer follow a window whose membership changes on every scroll.
   */
  const rowRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return;
      const observer = rowObserverFor();
      observer.observe(node);
      return () => observer.unobserve(node);
    },
    [rowObserverFor],
  );

  // Only on growth. Deleting a capture also changes the length, and scrolling to the bottom for it
  // would yank you away from the part of the log you had scrolled back to in order to prune it.
  const lastLength = useRef(0);
  useEffect(() => {
    if (entries.length > lastLength.current) logEndRef.current?.scrollIntoView({ block: "end" });
    lastLength.current = entries.length;
  }, [entries.length]);

  /**
   * The token the pivot falls inside — what the FORM strip describes.
   *
   * Still the TOKEN under a sub-token pivot (日本-512), and deliberately: the strip exists to say
   * why the text on screen does not match the dictionary, and the text on screen is still 弓道
   * when you are pointing at its 道. The dock answers 道場 beneath it; those are two different
   * questions and the screen is honest about both.
   */
  const selectedToken: ParsedToken | null = useMemo(() => {
    if (!selected) return null;
    const entry = entries.find((e) => e.id === selected.entryId);
    if (!entry) return null;
    return tokenAt(entry.tokens, selected.index)?.token ?? null;
  }, [selected, entries]);

  /**
   * Moving to another word ends the expanded panel's life, so the state it hangs off has to end
   * with it.
   *
   * `AddedPanel` only renders where `expanded.term` is still in `results`, so it was already
   * unmounting on a hover away — but `expanded` survived, and Ctrl+Z gates on nothing else. That
   * let undo reach an add from an hour ago with the panel long gone: a hard delete off a reflex
   * keystroke meant for something else.
   *
   * Keyed on the selection's VALUE, not the object. `Sentence` fires `onSelect` from
   * `onMouseEnter`, which builds a fresh `{entryId, index}` every time — so an identity dep would
   * close the panel for re-entering the very word you just added. And this cannot live in the
   * lookup effect below, which also depends on `entries`: `log.noteMined()` mutates those on add,
   * which would collapse the panel the instant it opened.
   *
   * Nothing is lost by clearing here. `AddedPanel` has no unmount save, so an unsaved field edit
   * already died the moment the results changed — the same trade `deleteEntry` documents. This is
   * bookkeeping catching up with what the screen has been doing all along.
   */
  const selectionKey = chipRoot
    ? `chip:${chipRoot.entryId}:${chipRoot.root}`
    : selected
      ? `${selected.entryId}:${selected.index}`
      : null;
  useEffect(() => {
    lookup.setExpanded(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  /**
   * A new capture also ends the panel's life, even though it leaves `selected` alone.
   *
   * `log.append` sets `focusedId` and `entries` and touches nothing else, so the effect above does
   * not fire and the panel stays open over the sentence you have just moved on from. That matters
   * for the paste rule below: the hotkey fires, a sentence arrives, you paste its screenshot — and
   * the panel would take it, putting the image on the card you added a minute ago and leaving the
   * new capture's slot empty. Pasting straight after a capture is the app's headline gesture.
   */
  useEffect(() => {
    lookup.setExpanded(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedId]);

  /**
   * Can this deployment translate? (日本-102)
   *
   * **Once, on mount** — not per log and not per row. It is a property of the deployment, and the
   * only thing on this screen that reads it is whether to draw a ghost button. A failure leaves it
   * `false`, which hides the affordance: on a screen whose whole point is reading, a control that
   * cannot work is worse than one that is not there.
   */
  useEffect(() => {
    let cancelled = false;
    void fetchContext()
      .then((context) => {
        if (!cancelled) setCanTranslate(context.configured);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /** A row that is no longer focused cannot keep its context popover — it is anchored to it. */
  useEffect(() => {
    setContextFor((current) => (current === focusedId ? current : null));
  }, [focusedId]);


  /**
   * The row images land on, and the only one drawn at full size (日本-306).
   *
   * Was `activeEntry` — "the newest capture" — and the rename is the ticket. Focus is now something
   * you can *move*: click a row's gutter, press `↑`/`↓`, or simply paste onto a row. A new capture
   * still takes it back, so the reading loop never has to be un-done.
   */
  /**
   * Which rows to render, and the padding standing in for the rest.
   *
   * `useMemo` on the pieces it reads, because the walk is O(log length) per scroll frame and
   * `entries` has a fresh identity on every capture. The ids are derived here rather than kept in
   * state: a second copy of the order is a second thing to keep in step with `byLog`.
   */
  const entryIds = useMemo(() => entries.map((entry) => entry.id), [entries]);
  const range = useMemo(
    () =>
      visibleRange({
        scrollTop: scroll.top,
        // Before the scroller has been measured there is no viewport to window against. A height
        // of 0 would render the overscan alone — so the first paint deliberately falls back to the
        // window, which is always at least as tall as the scroller inside it.
        viewportHeight: scroll.height || window.innerHeight,
        ids: entryIds,
        heights: rowHeights,
        estimate: isPhone ? ESTIMATED_ROW_PITCH.phone : ESTIMATED_ROW_PITCH.desktop,
        overscan: OVERSCAN,
      }),
    [scroll.top, scroll.height, entryIds, rowHeights, isPhone],
  );

  /**
   * Put a row at index `index` on screen, moving the scroller as little as possible.
   *
   * Needed because `↑`/`↓` can name a row the window has not drawn — see the key handler.
   *
   * **It reads the ids and the measurements through REFS, and that is what makes the `[]` honest.**
   * The key handler is one `keydown` listener re-registered on `entries`, so a callback closing
   * over `rowHeights` would be captured at whatever that map held the last time `entries` changed
   * — an empty map, on a log that has finished loading — and every arrow press would then scroll by
   * the estimate alone, past rows that had measured themselves since. A stable callback also keeps
   * the listener from being re-registered on every measurement batch.
   */
  const entryIdsRef = useRef<readonly string[]>(entryIds);
  entryIdsRef.current = entryIds;
  const rowHeightsRef = useRef<RowHeights>(rowHeights);
  rowHeightsRef.current = rowHeights;
  const rowEstimateRef = useRef(0);
  rowEstimateRef.current = isPhone ? ESTIMATED_ROW_PITCH.phone : ESTIMATED_ROW_PITCH.desktop;
  const scrollFocusIntoView = useCallback((index: number) => {
    const node = scrollerRef.current;
    if (!node) return;
    const next = scrollToRow({
      scrollTop: node.scrollTop,
      viewportHeight: node.clientHeight,
      ids: entryIdsRef.current,
      heights: rowHeightsRef.current,
      estimate: rowEstimateRef.current,
      index,
    });
    if (next !== null) node.scrollTop = next;
  }, []);

  /**
   * Find in the open log (日本-603). A hit becomes the FOCUSED row and scrolls into view through
   * the same arithmetic `↑`/`↓` use, so an undrawn row is reached exactly as an arrow reaches it —
   * and because it is the focused row, `A`, `T` and a paste all act on the sentence you found.
   */
  const { setFocused } = log;
  const jumpToHit = useCallback(
    (hit: Hit) => {
      setFocused(hit.entryId);
      scrollFocusIntoView(hit.row);
    },
    [setFocused, scrollFocusIntoView],
  );
  /** The first row on screen — where a fresh query starts searching from, as Ctrl+F does. */
  const topRow = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return 0;
    return visibleRange({
      scrollTop: node.scrollTop,
      viewportHeight: node.clientHeight,
      ids: entryIdsRef.current,
      heights: rowHeightsRef.current,
      estimate: rowEstimateRef.current,
      overscan: 0,
    }).start;
  }, []);
  const find = useLogFind({ entries, logId: log.activeLogId, onJump: jumpToHit, topRow });
  const showFind = find.show;

  /**
   * Parse what is on screen, and a little beyond it.
   *
   * The other half of the windowing (日本-602). `loadLog` hands the sentences over unparsed —
   * parsing a whole log up front was 2.6 s and a 9.6 MB response for 3,000 sentences, and once the
   * rendering was windowed it was the entire cost of opening one — so this is what gives the rows
   * being read their furigana, their underlines and their hover pivot.
   *
   * Keyed on the RANGE rather than on the scroll position, so the ordinary case of scrolling
   * within already-parsed rows asks for nothing: `parseTargets` returns an empty list and
   * `ensureParsed` returns immediately. `PARSE_AHEAD` is what keeps a normal scroll ahead of the
   * request rather than watching rows fill in.
   */
  useEffect(() => {
    if (entryIds.length === 0) return;
    log.ensureParsed(
      // No `known`/`inFlight` here on purpose: `ensureParsed` holds that record, because it is
      // what makes the requests. A second copy in this component would be a second thing to keep
      // in step and could only ever disagree.
      parseTargets({ ids: entryIds, start: range.start, end: range.end }),
    );
    // `ensureParsed` holds its own record of what it has asked about, so the entries themselves are
    // deliberately not a dependency: they change on every capture, and re-running this then would
    // re-derive the same already-answered list on every keystroke of a reading session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryIds, range.start, range.end, log.ensureParsed]);

  /**
   * Forget the pitch of a sentence that is gone.
   *
   * Ids are client-minted uuids, so nothing would ever collide with a stale entry — harmless for
   * a few, unbounded across a session of clearing and reopening logs.
   */
  useEffect(() => {
    setRowHeights((current) => heightsFor(current, entryIds));
  }, [entryIds]);

  const focusedEntry = entries.find((e) => e.id === focusedId) ?? entries[entries.length - 1];
  /**
   * The entry the dock's results came from. Mining inherits sentence + images from THIS entry —
   * the design lets you scroll back and mine an older capture, so the newest one is wrong to use.
   */
  const selectedEntry = selected
    ? entries.find((e) => e.id === selected.entryId)
    : focusedEntry;

  /**
   * Where a card mined here inherits its sentence and its two images.
   *
   * A function rather than a value, because `selectedEntry` moves under the results list without
   * the list re-rendering for it. Read supplies one; 検索 supplies none, and a card added there
   * simply has no sentence.
   */
  const mineSource = useCallback((): MineSource | null => {
    if (!selectedEntry) return null;
    return {
      text: selectedEntry.text,
      image: selectedEntry.image ?? "",
      extraImage: selectedEntry.extraImage ?? "",
      onMined: (noteId) => log.noteMined(selectedEntry.id, noteId),
    };
  }, [selectedEntry, log]);

  const editingEntry = editing ? entries.find((e) => e.id === editing.entryId) : undefined;

  const beginEdit = useCallback((entry: LogEntry, caret?: number) => {
    setEditing({ entryId: entry.id, text: entry.text, caret: caret ?? entry.text.length });
  }, []);

  const commitEdit = useCallback(() => {
    if (editing) void log.replaceText(editing.entryId, editing.text);
    setEditing(null);
    setPickerOpen(false);
  }, [editing, log]);

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setPickerOpen(false);
  }, []);

  /**
   * Drop a capture, and everything on the surface that was pointing at it.
   *
   * Five pieces of state key off an entry id, and each one strands differently if it is left:
   * a stale `editing` renders nothing but silently disables every shortcut, because the main
   * keydown handler bails while an edit is open; stale `selected` leaves the dock showing results
   * for a sentence that is no longer on screen, since the lookup effect returns early rather than
   * clearing; and `uploadError` re-attaches itself to whichever entry is active next.
   *
   * What is deliberately NOT cleared: `carded` and the `asked` ref. Neither is monotonic — `carded`
   * loses a term to an undo or to an `unknown` from the server, and a term in `asked` with no entry
   * in `carded` means the answer has not arrived. But both are keyed by term rather than by entry, and
   * the same word is very likely to appear in another capture, so dropping a sentence is no reason
   * to forget what its words cost a request to learn.
   */
  const deleteEntry = useCallback(
    (entry: LogEntry) => {
      if (editing?.entryId === entry.id) {
        setEditing(null);
        // The picker reads `editingEntry` for its caret label and writes back through
        // `replaceText`, so it cannot outlive the sentence it was opened to fix.
        setPickerOpen(false);
      }
      if (chipRoot?.entryId === entry.id) {
        // A chip outlives `selected` — it is its own question — so the sentence going away has to
        // clear it too, or the dock keeps answering a root pivoted from a log entry that is gone
        // and `selectionKey` holds that answer stable for as long as the tab is open.
        setChipRoot(null);
      }
      if (selected?.entryId === entry.id) {
        // Clearing the selection empties the results on its own — the hook's query goes null and
        // the list it indexes into goes with it.
        setSelected(null);
        setDockError(null);
        // Closed explicitly rather than left to unmount with the result list: AddedPanel does not
        // save on unmount, so an open card with unsaved edits would lose them either way — better
        // that it is a decision here than an accident of render order.
        lookup.setExpanded(null);
      }
      setUploadError(null);
      log.remove(entry.id);
    },
    [editing, selected, chipRoot, log],
  );

  /**
   * The one description of what a sentence row's menu does, read by both the `⋯` and the
   * right-click.
   *
   * A function rather than an array because the actions close over the row: two call sites, one
   * builder, so the gesture you happen to use cannot change what the menu offers.
   */
  const sentenceActions = useCallback(
    (entry: LogEntry) => ({
      logs: log.logs,
      folders: log.folders,
      openLogIds: log.openLogIds,
      currentLogId: log.activeLogId,
      onMove: (logId: string) => log.moveSentence(entry.id, logId),
      onChooseFromLibrary: () => onOpenLibrary(entry.id),
      onEdit: () => beginEdit(entry),
      onCopy: () => void navigator.clipboard?.writeText(entry.text).catch(() => undefined),
      onDelete: () => deleteEntry(entry),
    }),
    [log, onOpenLibrary, beginEdit, deleteEntry],
  );

  /** Track the caret on every interaction that can move it, so the picker always has a live one. */
  const syncCaret = useCallback((element: HTMLInputElement) => {
    const caret = element.selectionStart ?? element.value.length;
    setEditing((current) => (current ? { ...current, caret } : current));
  }, []);

  /**
   * Insert, re-parse in place, hand the dock back to results — and leave the edit state alive with
   * the caret after the character just placed, so assembling a two-kanji word is the same motion
   * twice rather than a fresh click into the sentence.
   */
  const insertKanji = useCallback(
    (kanji: string) => {
      if (!editing) return;
      const next = insertAtCaret(editing.text, editing.caret, kanji);
      setEditing({ entryId: editing.entryId, text: next.text, caret: next.caret });
      void log.replaceText(editing.entryId, next.text);
      setPickerOpen(false);
      // After the picker unmounts, not during: setSelectionRange on a field that has not been given
      // focus back yet silently does nothing.
      requestAnimationFrame(() => {
        const input = editRef.current;
        if (!input) return;
        input.focus();
        input.setSelectionRange(next.caret, next.caret);
      });
    },
    [editing, log],
  );

  /**
   * Store the file, then point the sentence's slot at it. The slot holds the Anki *field value*
   * rather than a blob URL, so every card mined from the sentence can inherit it verbatim without
   * a second upload — and so it survives the card being added minutes later.
   */
  const attachImage = useCallback(
    async (entryId: string, which: SlotKey, file: File) => {
      setUploading({ entryId, which });
      setUploadError(null);
      try {
        const stored = await storeMedia(
          mediaFilename(file.type, Date.now(), suffixForSlot(which)),
          await fileToBase64(file),
        );
        log.setImage(entryId, which, stored.value);
      } catch (error) {
        setUploadError(error instanceof Error ? error.message : "画像を保存できませんでした。");
      } finally {
        setUploading(null);
      }
    },
    [log],
  );

  /**
   * Moving an image between two slots in the reading log.
   *
   * Both ends are session state, so this is two `setImage` calls and no request at all — the value
   * being moved is a string naming a file that already exists. Nothing is uploaded, nothing is
   * copied in the media store, and nothing can half-succeed.
   *
   * A drop whose source is no longer in the log is dropped silently: the only way to get one is to
   * delete the sentence you are dragging from mid-gesture, and the honest response to that is the
   * image staying where it is.
   */
  const dragPayload = useCallback(
    (entryId: string, which: SlotKey): string | null => {
      const entry = log.entries.find((e) => e.id === entryId);
      const value = which === "extraImage" ? entry?.extraImage : entry?.image;
      if (!value) return null;
      const ref: SlotRef = {
        owner: { kind: "sentence", id: entryId },
        slot: which,
        value,
        filename: parseImageFilename(value),
      };
      return JSON.stringify(ref);
    },
    [log.entries],
  );

  const dropSlot = useCallback(
    (entryId: string, which: SlotKey, payload: string) => {
      let from: SlotRef;
      try {
        from = JSON.parse(payload) as SlotRef;
      } catch {
        return;
      }
      // Only log-to-log moves land here. A drag out of the added-card panel targets a note, which
      // needs a request and belongs to whoever owns that note — not to this surface.
      if (from.owner.kind !== "sentence") return;

      const entry = log.entries.find((e) => e.id === entryId);
      if (!entry) return;
      const value = which === "extraImage" ? entry.extraImage : entry.image;
      const move = planSlotMove(from, {
        owner: { kind: "sentence", id: entryId },
        slot: which,
        value,
        filename: parseImageFilename(value),
      });
      for (const assignment of move.kind === "noop" ? [] : move.assign) {
        if (assignment.owner.kind !== "sentence") continue;
        log.setImage(assignment.owner.id, assignment.slot, assignment.value);
      }
    },
    [log],
  );

  // Ctrl+V / Ctrl+Shift+V land on the ACTIVE capture, wherever focus happens to be — you paste a
  // screenshot straight after the hotkey fires, without clicking into anything first.
  //
  // UNLESS the added-card panel is open, in which case it owns the paste and this yields. Both
  // listeners are on `window`, and `Read` mounts first — so `defaultPrevented` cannot be used to
  // give the panel precedence the way `AddedPanel`'s keydown does. The check is the contract, and
  // it is commented on both sides: exactly one of the two ever acts.
  //
  // The test is whether the panel is actually ON SCREEN, not merely whether `expanded` is set. The
  // panel only renders where its term is still in `results`, so guarding on the state alone would
  // yield the paste to a panel that is not there — and nothing would take it.
  //
  // **The predicate has to be the render condition, character for character.** It used to be
  // `results.some(r => r.term === expanded.term)`, which matched the render check exactly while
  // that check was "first row with this term". Since 日本-290 the panel is anchored by ROW, and a
  // term can span several rows — so a `results` list that changed under a surviving `expanded`
  // (editing the sentence text re-runs the lookup without touching `selected`) can hold the term
  // at some other index, leaving `some()` true with no panel mounted and the paste going nowhere.
  //
  // Read through a ref rather than a dep so this does not re-register on every expand and collapse.
  const panelShownRef = useRef(false);
  panelShownRef.current =
    expanded !== null &&
    (() => {
      const at = dockRows[expanded.row];
      return at?.kind === "vocab" && at.entry.term === expanded.term;
    })();
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (panelShownRef.current) return;
      // **The library replaces this whole surface but does not unmount it**, so every window
      // listener in this component stays armed behind it — the early `return <Library/>` is below
      // all the hooks. Without this, pasting a screenshot while filing your logs would attach it,
      // silently, to the focused sentence of a log that is not even on screen.
      if (libraryOpen) return;
      // The panel check above arbitrates between the two window listeners; this one is a different
      // question, and the sentence edit is the field it protects. A browser copy carries text/html
      // AND an image, so without this, pasting into the sentence edit (or the compose row) would
      // swallow the text and hang the page's illustration on the capture instead.
      if (isTextEntry(event.target)) return;
      const file = firstImage(event.clipboardData?.files);
      if (!file || !focusedEntry) return;
      event.preventDefault();
      // The clipboard event carries no modifier state, so the last keydown decides the slot.
      void attachImage(focusedEntry.id, slotForPaste(shiftHeld.current), file);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [focusedEntry, attachImage, libraryOpen]);

  const openPicker = useCallback(() => {
    // Alt+R with no caret anywhere: put one at the end of the sentence you are reading, since that
    // is the only field the picker could mean.
    if (!editing) {
      if (!focusedEntry) return;
      beginEdit(focusedEntry);
    }
    setPickerOpen(true);
  }, [editing, focusedEntry, beginEdit]);

  /**
   * `A`: add the dock's focused definition as a card. True when it did.
   *
   * **One function for the key and for the mouse button** (日本-710), so the two cannot come to
   * disagree about when adding is allowed. It asks `dictionaryAction` about a bare `a` rather than
   * restating its rule: that function is what the key obeys and what `ActionButton` draws from, and
   * a copy of it here would be the third place to keep in step.
   */
  const addFocused = useCallback((): boolean => {
    // `rows`, not `results` — see the keydown below.
    const row = dockRows[focusedResult];
    if (row?.kind !== "vocab") return false;
    const focused = row.entry;
    const rowText = rowTerm(row) ?? "";
    const action = dictionaryAction(
      { key: "a", ctrlKey: false, metaKey: false, altKey: false },
      {
        hasFocus: true,
        signal: carded.get(rowText) ?? { state: "unknown" },
        onCard: isOnCard(lookup.onCardEntries, focused.term, focused.seq),
        isGrammar: false,
        composing: false,
      },
    );
    if (action !== "add") return false;
    void lookup.add(focused, focusedResult, mineSource());
    return true;
  }, [dockRows, focusedResult, carded, lookup, mineSource]);

  /**
   * `T`: translate the focused sentence (日本-102). True when it did.
   *
   * Guarded on `canTranslate` so the key does exactly what the button does — on a deployment with
   * no API key neither exists, rather than the key silently erroring where no affordance was ever
   * drawn. Shared with the mouse button for `addFocused`'s reason.
   */
  const translateFocused = useCallback((): boolean => {
    if (!canTranslate || !focusedEntry || focusedEntry.translating) return false;
    void log.translate(focusedEntry.id);
    return true;
  }, [canTranslate, focusedEntry, log]);

  /**
   * `↑`/`↓`: move the sentence focus (日本-306). Shared with the global shortcut (日本-729) for
   * `addFocused`'s reason.
   *
   * **It also pivots onto the new sentence's first word** (日本-729, Alex), so the dock answers for
   * the row you arrived at rather than the one you left — from a controller there is no pointer to
   * hover with, and a move that left the dock behind would need a second press to be useful.
   * Skipped while Ask holds the dock, on `selectPivot`'s reasoning, and while the row is still
   * unparsed, rather than clearing a selection there is nothing to replace with.
   */
  const moveSentence = useCallback(
    (step: 1 | -1) => {
      const at = entries.findIndex((e) => e.id === focusedId);
      // From nothing, ↑ starts at the newest and ↓ at the oldest — you arrow *into* the log from
      // the end you are moving away from.
      const next = at === -1 ? (step === 1 ? 0 : entries.length - 1) : at + step;
      const index = Math.min(Math.max(next, 0), entries.length - 1);
      const target = entries[index];
      if (!target) return;
      log.setFocused(target.id);
      // **The target may not be rendered at all** (日本-602), so this stopped being optional the
      // moment the list was windowed: arrowing off the bottom of the window used to move the focus
      // to a row nothing had drawn, which reads as a key that does nothing.
      //
      // Scrolling by ARITHMETIC rather than `scrollIntoView`, because there is no element to call
      // it on until the window has moved — and the window only moves in response to the scroll.
      // `logWindow`'s own padding arithmetic gives the offset directly.
      scrollFocusIntoView(index);
      // Not when the selection is already in that row — which is every press that clamps at the
      // end of the log. Reading the newest capture, a stray ↓ would otherwise throw away the word
      // you were on for its first one.
      if (askModeRef.current || selected?.entryId === target.id) return;
      const first = nextWord(entries, null, 1, target.id);
      if (first && first.entryId === target.id) {
        setChipRoot(null);
        setSelected(first);
      }
    },
    [entries, focusedId, log, scrollFocusIntoView, selected?.entryId],
  );

  /**
   * ⇥ / ⇧⇥: the next or previous word of the FOCUSED sentence (日本-729 made it the focused one —
   * see `nextWord`). Shared with the global shortcut.
   *
   * **It takes the dock back from a chip, exactly as a sentence hover does.** Without this the
   * pivot moves, the strip re-describes the new token and the dock goes on answering しまう — so ⇥
   * looks like a key that does nothing at all: the highlight is suppressed while a chip holds the
   * dock, and the chip it names is no longer on the strip.
   */
  const moveWord = useCallback(
    (step: 1 | -1) => {
      setChipRoot(null);
      setSelected((current) => nextWord(entries, current, step, focusedId));
    },
    [entries, focusedId],
  );

  /** `j`/`k`: move the dock's focused result. Clamped, not wrapped. Shared with the global shortcut. */
  const moveResult = useCallback(
    (step: 1 | -1) => {
      lookup.setFocusedResult((i) =>
        step === 1 ? Math.min(i + 1, Math.max(dockRows.length - 1, 0)) : Math.max(i - 1, 0),
      );
    },
    [lookup, dockRows.length],
  );

  // A add, J/K move, Tab next word — shortcuts shown on the controls, never as a legend.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // **The library replaces this surface but does not unmount it** — the early
      // `return <Library/>` sits below every hook — so without this the whole shortcut set stays
      // live behind it. `A` would add a card from a results list nothing is rendering while you are
      // renaming a log; `↑`/`↓` would move the focus of a log that is not on screen; `Tab` would be
      // preventDefault'd out from under the library's own search box and buttons; and Alt+R would
      // open the radical picker over it. Ahead of the Alt+R branch for that last reason.
      if (libraryOpen) return;
      // Alt+R is the one shortcut that works *while* editing — it is reached from a caret.
      if (event.altKey && (event.key === "r" || event.key === "R")) {
        event.preventDefault();
        if (pickerOpen) setPickerOpen(false);
        else openPicker();
        return;
      }
      if (editing !== null) return;
      // **Ctrl+F opens the log's own find** (日本-603), and it sits ABOVE the text-entry rung so it
      // works from the compose field too. The browser's find is wrong here twice over — the list
      // is windowed, and furigana splits matches on the rows it can see — and the desktop shell
      // has none at all. Every other surface keeps the browser's.
      if (
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        !event.shiftKey &&
        (event.key === "f" || event.key === "F")
      ) {
        event.preventDefault();
        showFind();
        return;
      }
      // TEXTAREA as well as INPUT: the added-card panel's English field is a textarea (it holds the
      // engine's newline-separated senses), and without it `j`, `k` and `Tab` are swallowed by
      // these shortcuts and never reach the field you are typing into.
      if (isTextEntry(event.target)) return;

      // Ctrl+Z undoes the add you just made. Not a general app-wide undo — the design is explicit
      // that undo is a short, local affordance, and here it reaches exactly one action.
      if ((event.ctrlKey || event.metaKey) && (event.key === "z" || event.key === "Z")) {
        if (expanded?.justAdded) {
          event.preventDefault();
          void lookup.undo();
        }
        return;
      }

      // **`rows`, not `results`** — `focusedResult` names a position on SCREEN, and since 日本-498
      // the dock holds grammar rows as well. Indexing the dictionary rows alone put `A` on
      // whichever entry happened to sit at that index in the shorter list, which is a different
      // word. Same correction as 検索's, and for the same reason.
      const row = dockRows[focusedResult];
      const focused = row?.kind === "vocab" ? row.entry : undefined;
      const rowText = row ? (rowTerm(row) ?? "") : "";

      // `a` / `e` / `j` / `k` over the results — the rule is shared with 検索 and lives in
      // `dictionaryKeys.ts`, pure and tested. The ladder above has already returned for an open
      // editor and for any text entry, so what reaches here is a bare key over the log.
      const action = dictionaryAction(event, {
        hasFocus: row !== undefined,
        signal: carded.get(rowText) ?? { state: "unknown" },
        onCard: isOnCard(lookup.onCardEntries, focused?.term ?? null, focused?.seq ?? 0),
        isGrammar: row?.kind === "grammar",
        composing: event.isComposing,
      });
      // **`O` opens a 文法 row in 文法** (日本-519). It was `A`, and it made a card.
      if (action === "open" && row?.kind === "grammar") {
        event.preventDefault();
        onOpenGrammar(row.entry.id);
        return;
      }
      if (action === "add" && focused) {
        // Through `addFocused`, which the global shortcut (日本-710) calls too.
        if (addFocused()) event.preventDefault();
        return;
      }
      // Guarded on the ROW rather than on `focused` because it was once reachable on a 文法 row
      // too (日本-498); `dictionaryAction` refuses `E` there since 日本-519, so what reaches here
      // is a vocabulary row — the guard is kept because the shape is right, not because it fires.
      if (action === "edit" && row) {
        event.preventDefault();
        // Cleared here rather than inside the hook's `edit`, which `add` also calls right after
        // setting the "already in your deck" message — clearing in there would erase it. A failed
        // `E` on one row otherwise leaves its message sitting over the next row's panel.
        setDockError(null);
        void lookup.edit(rowText, focusedResult);
        return;
      }
      // `j`/`k` only — the ARROWS belong to the sentence focus here (日本-306), and the branch
      // below is what keeps them. `dictionaryAction` answers `next`/`prev` for both pairs, so the
      // key is re-checked rather than the action trusted.
      if ((action === "next" || action === "prev") && (event.key === "j" || event.key === "k")) {
        event.preventDefault();
        moveResult(action === "next" ? 1 : -1);
        return;
      }

      if (event.key === "/" && (event.ctrlKey || event.metaKey)) {
        // **Point Ask at the focused sentence.** The gutter's `?` does the same thing; this is the
        // half that keeps a hand on the keyboard, which is where the reading loop lives.
        //
        // Ctrl-modified, unlike `a`/`e`/`j`/`k`/`t`, because `/` is a character a question can
        // contain and a bare one would be swallowed the moment the composer had focus.
        if (ask.available && focusedEntry) {
          event.preventDefault();
          ask.setMode("ask");
          ask.point({ sentenceId: focusedEntry.id, text: focusedEntry.text });
        }
      } else if (event.key === "t" || event.key === "T") {
        // **`T` translates the focused sentence** (日本-102), which is what the ghost button on
        // that row advertises. Bare, like `a`, `e`, `j` and `k`, for their reason: the log has no
        // text field with focus by default, and the ladder above has already returned for an open
        // editor, a text entry and the library. The guards are `translateFocused`'s.
        if (translateFocused()) event.preventDefault();
      } else if ((event.key === "l" || event.key === "L") && !event.ctrlKey && !event.metaKey) {
        // The strip's `冊` button advertises this. Not Ctrl-anything: the log has no text field
        // with focus by default, which is the same reason `a`, `e`, `j` and `k` are bare.
        //
        // The modifier test is the other half of that, and unlike the bare letters above it is not
        // optional: Ctrl/⌘+L is *focus the address bar* in every browser, and preventing it here
        // would break it on the one screen the app is normally sitting on.
        event.preventDefault();
        onOpenLibrary();
      } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        // **The arrows move the SENTENCE focus** (日本-306), which is the one keyboard conflict
        // this ticket forced. They used to be aliases for `j`/`k` on the dock's result list; the
        // vim pair keeps that job alone, and the dock's hint says `jk move` rather than `↑↓ move`.
        //
        // Focus decides where a pasted image lands, so having a key for it matters: reaching for
        // the mouse to click a row you are about to paste onto defeats the gesture.
        event.preventDefault();
        moveSentence(event.key === "ArrowDown" ? 1 : -1);
      } else if (event.key === "Tab") {
        // ⇥ next word — the dock's own hint promises it. Sweeping a sentence by hand means moving
        // the mouse; this is how you read one straight through without leaving the keyboard.
        event.preventDefault();
        moveWord(event.shiftKey ? -1 : 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    dockRows, focusedResult, carded, lookup, mineSource, setDockError, editing, pickerOpen,
    openPicker, expanded, libraryOpen,
    focusedEntry, ask, addFocused, translateFocused, showFind, moveSentence, moveWord, moveResult,
  ]);

  /**
   * The global Read shortcuts (日本-710, 日本-729): a controller button doing `A`, `T` or one of the
   * moves while the novel beside this window has the focus. The tray agent relays the press; this
   * runs the same function the key runs.
   *
   * **Keyed on the nonce, and the nonce at MOUNT is already spent.** `App` keeps the last action
   * for the life of the tab and Read unmounts on every other surface, so an effect that ran on
   * mount would replay a press from before you left — adding a card you never asked for on the way
   * back to Read.
   *
   * **The ladder is the keydown's, less one rung.** The library and an open sentence editor still
   * stop it, and Ask holding the dock stops `add` and the result moves, because there are no
   * definitions on screen to add or to move between.
   * `isTextEntry` does not: this press never reached a text field, and a field that merely still
   * held focus when you looked away to the novel would otherwise make the button do nothing.
   */
  const seenAction = useRef(agentAction?.nonce ?? 0);
  useEffect(() => {
    if (!agentAction || agentAction.nonce === seenAction.current) return;
    seenAction.current = agentAction.nonce;
    if (libraryOpen || editing !== null) return;
    const dockIsLookup = !askModeRef.current;
    switch (agentAction.name) {
      case "add":
        if (dockIsLookup) addFocused();
        break;
      case "translate":
        translateFocused();
        break;
      case "sentence-prev":
      case "sentence-next":
        moveSentence(agentAction.name === "sentence-next" ? 1 : -1);
        break;
      case "word-prev":
      case "word-next":
        moveWord(agentAction.name === "word-next" ? 1 : -1);
        break;
      case "result-prev":
      case "result-next":
        if (dockIsLookup) moveResult(agentAction.name === "result-next" ? 1 : -1);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentAction?.nonce]);

  const logRegion = (
    <div
      className={
        isPhone
          ? "flex flex-col gap-[18px] px-4 pt-[14px]"
          : "flex flex-col gap-[22px] px-[26px] pt-5"
      }
    >
      {log.error && (
        // Writes are optimistic, so the sentence is on screen either way — what this says is that
        // it is not saved. Never a rollback: being told your reading vanished is worse than being
        // told it is unsaved, and the text is right there to copy if it matters.
        <div className="flex items-start gap-2 rounded-[var(--r-2)] border border-[color:var(--accent-line)] bg-[color:var(--void)] px-3 py-2">
          <span className="flex-1 text-meta text-[color:var(--accent-hi)]">{log.error}</span>
          <button
            type="button"
            onClick={log.dismissError}
            aria-label="閉じる"
            className="text-[14px] text-ink-4 hover:text-ink-2"
          >
            ×
          </button>
        </div>
      )}

      {entries.length === 0 && (
        <p className="text-secondary text-ink-3">
          まだ何も取り込んでいません。ホットキーで読み取った文がここに届きます。
        </p>
      )}

      {/* **The two spacers are PADDING on this wrapper, never spacer elements** (日本-602).
          A spacer div would be a flex child of the gapped column and pick up a gap of its own on
          each side, putting 22px of drift at both ends of every window. As padding the three parts
          sum to exactly the un-windowed height — `logWindow.test.ts` asserts that arithmetic.

          Its own wrapper rather than padding the column itself, because the column also holds the
          error banner and the compose row, which must keep their own 22px gaps. */}
      <div
        className={isPhone ? "flex flex-col gap-[18px]" : "flex flex-col gap-[22px]"}
        style={{ paddingTop: range.padTop, paddingBottom: range.padBottom }}
      >
      {entries.slice(range.start, range.end).map((entry) => {
        const isFocused = entry.id === focusedEntry?.id;
        return (
          // **The whole row is the focus target except the text** (日本-306). Clicking the sentence
          // still opens it for editing — that shipped and cannot become ambiguous — so the gutter,
          // the space beside the sentence and this padding are what take focus instead.
          //
          // Draggable onto a tab in the strip, which is the same move the `⋯` menu offers.
          <div
            key={entry.id}
            // Both are the windowing's (日本-602): the ref is how the row's pitch gets measured,
            // and `data-sentence-id` is how the observer knows which row reported it. The attribute
            // is also what `checks/check-read.mjs` counts to assert the window stays bounded.
            ref={rowRef}
            data-sentence-id={entry.id}
            className="group flex cursor-default gap-3 rounded-[var(--r-2)] py-[2px] hover:bg-[color:var(--line-soft)]"
            style={{ opacity: isFocused ? 1 : 0.58 }}
            onClick={() => log.setFocused(entry.id)}
            // The same items the `⋯` offers, from one builder so the two cannot drift. Focusing
            // the row first is what makes them act on what the pointer is on rather than on
            // whichever row happened to be focused.
            onContextMenu={(event) => {
              if (!shouldOpenContextMenu(event.nativeEvent, { selectionInTarget: hasSelectionIn(event.currentTarget) })) return;
              event.preventDefault();
              event.stopPropagation();
              log.setFocused(entry.id);
              menu.openAt(
                { x: event.clientX, y: event.clientY },
                sentenceMenuItems(sentenceActions(entry)),
              );
            }}
            draggable
            onDragStart={(event) => {
              // **A slot image is draggable too, and its `dragstart` BUBBLES to here.** Without
              // this guard the row stamps `SENTENCE_DRAG_TYPE` onto a drag that was only ever
              // about one screenshot — and since `TabStrip` accepts anything carrying that type,
              // dragging a picture onto another tab moved the whole sentence to another log.
              // Checked on the transfer rather than on `event.target`, because a row drag can
              // legitimately start on any of the row's non-draggable children (the clock, the
              // padding, the space beside the sentence).
              if (event.dataTransfer.types.includes(SLOT_DRAG_TYPE)) return;
              event.dataTransfer.setData(SENTENCE_DRAG_TYPE, entry.id);
              event.dataTransfer.effectAllowed = "move";
            }}
          >
            {/* The gutter carries the row's own controls: the clock, and under it the `⋯` menu.
                Keeping them here rather than beside the text leaves the sentence's measure and the
                12px gap untouched, and reads as a handle for the whole row.

                **42px, and it is MEASURED off the clock rather than chosen.** `HH:MM` is five
                characters of IBM Plex Mono, which advances 0.6em, so the string is exactly
                `5 × 0.6 × 14 = 42px` — and the span is the cross-axis size of a `flex-col` under
                `items-end`, so anything narrower does not truncate or wrap, it overflows LEFT, out
                of the row and past the surface's edge. Measured in a real browser against the
                built CSS: at the 14px floor the old widths put the clock's box at x = −4 (desktop,
                38px) and x = −10 (phone, `w-8`). It is the same failure `lib/dates.ts` records as
                the reason the clock is 24-hour at all, arriving from the other direction — there
                the string grew, here the type did.

                One width for both, because the number that decides it is the same on both. */}
            <div className="flex w-[42px] shrink-0 flex-col items-end font-mono text-[14px]/[2.4]">
              <span style={{ color: isFocused ? "var(--accent)" : "var(--ink-4)" }}>
                {clock(entry.at)}
              </span>
              {/* **`?` points Ask at this sentence**, and only the focused row offers it — a
                  button on every line turns a page of reading into a page of buttons, which is the
                  same argument that keeps `訳 翻訳` to the focused row. */}
              {isFocused && ask.available && (
                <button
                  type="button"
                  onClick={() => {
                    ask.setMode("ask");
                    ask.point({ sentenceId: entry.id, text: entry.text });
                  }}
                  title="この文について質問"
                  aria-label="この文について質問"
                  className="my-[2px] flex h-[19px] w-[19px] items-center justify-center rounded-full border border-accent-line bg-accent-tint text-[14px]/[1] font-semibold text-accent-text"
                >
                  ?
                </button>
              )}
              {/* Always visible on a phone, where there is no hover to reveal it. */}
              <SentenceMenu sentenceId={entry.id} isPhone={isPhone} {...sentenceActions(entry)} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              {editing?.entryId === entry.id ? (
                <div className="flex items-start gap-[9px]">
                  <input
                    ref={editRef}
                    autoFocus
                    value={editing.text}
                    onChange={(e) => {
                      const { value, selectionStart } = e.currentTarget;
                      setEditing({
                        entryId: entry.id,
                        text: value,
                        caret: selectionStart ?? value.length,
                      });
                    }}
                    onSelect={(e) => syncCaret(e.currentTarget)}
                    onClick={(e) => syncCaret(e.currentTarget)}
                    onKeyUp={(e) => syncCaret(e.currentTarget)}
                    onBlur={() => {
                      // Opening the picker moves focus out of this field. Committing here would
                      // close the edit state the picker exists to feed.
                      if (!pickerOpen) commitEdit();
                    }}
                    onKeyDown={(e) => {
                      // preventDefault marks the key as answered, so window-level handlers (the
                      // expanded card's save-and-collapse Escape) leave this edit's keys alone.
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitEdit();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelEdit();
                      }
                    }}
                    className="min-w-0 flex-1 rounded-[var(--r-3)] border-[1.5px] border-accent bg-panel px-[13px] py-[9px] font-jp text-[29px]/[1.85] outline-none"
                  />
                  {/* Lit, and beside the field rather than in a toolbar: it belongs to this caret.
                      On a phone the compose row carries `部` instead — there is no Alt key to
                      advertise, and the sentence needs the width. */}
                  {!isPhone && (
                  <button
                    type="button"
                    // mousedown, not click — click lands after blur, which would have closed the edit.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setPickerOpen(!pickerOpen);
                    }}
                    aria-pressed={pickerOpen}
                    title="部品検索"
                    className="flex h-[52px] w-[52px] shrink-0 flex-col items-center justify-center gap-[2px] rounded-[var(--r-3)] border-[1.5px] border-accent bg-accent-tint shadow-[var(--sh-frame)]"
                  >
                    {/* `--accent-text`, NOT `--accent-tint`. The old value was `--action-darkest`,
                        which the compatibility block maps to `--accent-tint` — and this button's
                        own ground is `bg-accent-tint`, so the mechanical substitution painted the
                        glyph in exactly its own background and 部 disappeared. */}
                    <span className="font-jp text-[23px]/[1] text-[color:var(--accent-text)]">
                      部
                    </span>
                    <span className="font-mono text-[14px]/[1] font-semibold text-[color:var(--accent-deep)]">
                      Alt+R
                    </span>
                  </button>
                  )}
                </div>
              ) : (
                // Hover selects a word; clicking the sentence enters the edit state. OCR is
                // imperfect and this is the fix, so it must not need a discovered gesture.
                <div
                  onClick={(event) => {
                    // The one part of the row that is NOT a focus target. Stopped here rather than
                    // by checking the target on the row's handler, so the exception is stated where
                    // the exception is.
                    event.stopPropagation();
                    log.setFocused(entry.id);
                    beginEdit(entry);
                  }}
                  title="クリックで文を修正"
                >
                  <Sentence
                    entryId={entry.id}
                    tokens={entry.tokens}
                    text={entry.text}
                    // **The highlight follows the ANSWER, not the pointer** (日本-512). Handing
                    // it to the pivoted sentence only: every other one re-renders — per character
                    // now — each time the dock settles, for a highlight it cannot draw.
                    match={
                      !chipRoot &&
                      selected?.entryId === entry.id &&
                      matchOwner.entryId === entry.id
                        ? matchOwner.match
                        : null
                    }
                    signal={carded}
                    // **Suspended while Ask holds the dock.** The dock is single-tenant, so a
                    // lookup fired here has nowhere to render — and switching the segment back on
                    // hover would eat a half-typed question. Pointing at a sentence is the
                    // gutter's `?` and `Ctrl+/` in that mode.
                    onSelect={selectPivot}
                    // `6c` runs a size smaller throughout: 24/20 against the desktop 30/24.
                    fontSize={isPhone ? (isFocused ? 24 : 20) : isFocused ? 30 : 24}
                    findMarks={find.marks.get(entry.id)?.text}
                    selectOnTap={isPhone}
                  />
                </div>
              )}

              {/* The English, under the Japanese and above the slots (日本-102, frame `1a`).
                  Aligned to the sentence rather than to the 38px gutter: it is a reading of *this*
                  sentence, and indenting it to the gutter would make it look like a row of its own.

                  Rendered on every row, but it draws nothing at all on an unfocused untranslated
                  one — translation is never automatic, and a button on every line would turn a
                  page of reading into a page of buttons. */}
              <div className="relative">
                <Translation
                  // Only the row whose panel is open, so one ref can serve the whole list.
                  contextButtonRef={contextFor === entry.id ? contextAnchor : undefined}
                  translation={entry.translation}
                  findMarks={find.marks.get(entry.id)?.translation}
                  translating={entry.translating}
                  focused={isFocused}
                  configured={canTranslate}
                  isPhone={isPhone}
                  onTranslate={() => {
                    log.setFocused(entry.id);
                    void log.translate(entry.id);
                  }}
                  onChange={(text) => log.setTranslation(entry.id, text)}
                  onOpenContext={() =>
                    setContextFor((current) => (current === entry.id ? null : entry.id))
                  }
                />
                {contextFor === entry.id && (
                  <ContextPanel
                    // **By sentence**, which is the only scope that can price the envelope — the
                    // server resolves the log and the folder from it.
                    scope={{ sentenceId: entry.id }}
                    onClose={() => setContextFor(null)}
                    // `context ▾` is a toggle and lives outside the panel, so the panel has to be
                    // told not to treat pressing it as an outside click.
                    anchor={contextAnchor}
                    className="absolute left-0 top-full mt-1"
                  />
                )}
              </div>

              <ImageSlots
                image={{ value: entry.image, filename: parseImageFilename(entry.image) }}
                extraImage={{
                  value: entry.extraImage,
                  filename: parseImageFilename(entry.extraImage),
                }}
                // **The large slots ARE the focus indicator** (日本-306). Exactly one row in the log
                // shows the two 148×64 drop zones; every other shows 74×42 thumbnails. No border,
                // no bar, no highlight — the screen already carries three underline states, and a
                // second visual language for focus would be noise.
                compact={!isFocused}
                // **Every row accepts a drop, and dropping focuses it.** This reverses the old rule
                // that only the newest capture was editable ("the images belong to the moment they
                // were taken"), which is exactly what 日本-306 was raised to undo: a screenshot has
                // to be able to land on a sentence you read ten minutes ago. Focus moves in the
                // same gesture, so the common case needs no gesture at all.
                onFile={(which, file) => {
                  log.setFocused(entry.id);
                  void attachImage(entry.id, which, file);
                }}
                onClear={(which) => log.setImage(entry.id, which, "")}
                onSlotDragStart={(which) => dragPayload(entry.id, which)}
                onSlotDrop={(which, payload) => {
                  log.setFocused(entry.id);
                  dropSlot(entry.id, which, payload);
                }}
                // Two sources, and both are real. `uploading` is a paste happening in this
                // component right now; `entry.imageBusy` is the tray agent's screenshot, which is
                // uploading from another process entirely and can land on a capture you have since
                // scrolled past — so it has to be per entry rather than per surface.
                busy={
                  uploading?.entryId === entry.id ? uploading.which : (entry.imageBusy ?? null)
                }
                isPhone={isPhone}
              />

              {isFocused && uploadError && (
                // Storing an image needs the sync hub. Saying so beats a slot that silently stays
                // empty, because the paste itself gave no other feedback.
                <span className="text-meta text-[color:var(--ink-3)]">{uploadError}</span>
              )}

              {entry.minedNoteIds.length > 0 && (
                <span className="font-mono text-[14px]/[1.4] text-ink-4">
                  {entry.minedNoteIds.length}枚作成
                </span>
              )}
            </div>
          </div>
        );
      })}
      </div>

      {/* Desktop only — on a phone the compose row above the log is already this. The wrapper keeps
          the scroll anchor, so a submitted sentence still scrolls the field back into view.

          It sits AFTER the windowed wrapper, so `padBottom` is between it and the last rendered
          row — which is what keeps the growth autoscroll landing in the right place. */}
      <div ref={logEndRef} className={isPhone ? "" : "pl-[50px]"}>
        {!isPhone && <AddSentence onSubmit={(text) => void log.append(text)} />}
      </div>
    </div>
  );

  // The picker takes the dock rather than a modal, so the sentence and the hole you are filling
  // stay on screen above it. Esc drops back to results with the edit and the selection intact.
  const dock = pickerOpen ? (
    <RadicalPicker
      caretLabel={editingEntry ? clock(editingEntry.at) : "この文"}
      selected={radicals}
      onSelectedChange={setRadicals}
      recents={recentRadicals}
      onRemember={(radical) => setRecentRadicals((current) => rememberRadical(current, radical))}
      onInsert={insertKanji}
      onClose={() => setPickerOpen(false)}
      compact={isPhone}
    />
  ) : (
    <div
      className={
        isPhone
          ? "flex h-full flex-col gap-[10px]"
          : "flex h-full flex-col gap-[10px] px-[26px] pt-[13px]"
      }
    >
      <div className="flex shrink-0 items-center gap-[10px]">
        <span className="font-jp text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
          {selectedToken?.surface ?? "—"}
        </span>
        {/* **Each kind counted separately** (日本-498), on 検索's reasoning: one total cannot say
            whether the grammar dictionary answered, and 文法0件 is noise on a word that is not a
            construction — so the row is omitted rather than drawn as a zero. */}
        <span className="text-meta text-ink-4">
          {dockKinds}
          {selectedToken && !selectedToken.inflected && " · 活用なし"}
        </span>
        {/* Drawn only when there IS something to tell apart. On a word the grammar dictionary
            could not answer the control would be three buttons where two do nothing, which reads
            as a broken filter rather than as an honest answer. */}
        {allDockRows.some((row) => row.kind === "grammar") && (
          <div
            data-check="dock.kinds"
            className="flex shrink-0 items-center gap-[2px] rounded-[var(--r-2)] border border-line bg-raised p-[2px]"
          >
            {(["all", "vocab", "grammar"] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                data-check="dock.kind"
                data-kind={kind}
                data-on={dockKind === kind ? "1" : undefined}
                onClick={() => setDockKind(kind)}
                className={`rounded-[var(--r-2)] px-[11px] py-[5px] font-jp text-meta font-semibold ${
                  dockKind === kind ? "bg-accent text-ink-bright" : "text-ink-3"
                }`}
              >
                {kind === "all" ? "すべて" : KIND_LABEL[kind]}
              </button>
            ))}
          </div>
        )}
        <span className="h-px flex-1 bg-line" />
        {/* Keyboard hints belong beside the control they trigger — and there is no keyboard here. */}
        {!isPhone && (
          <span className="font-mono text-meta text-ink-4">
            {/* `jk`, not `↑↓`: since 日本-306 the arrows move the sentence focus in the log, and a
                hint promising them here would be a lie about the key that has the bigger effect. */}
            jkで移動 · Aで追加 · Oで開く · ⇥で次の語
          </span>
        )}
      </div>

      {selectedToken && (
        <FormStrip
          token={selectedToken}
          activeRoot={chipRoot?.root ?? null}
          onPivot={(root) =>
            selected &&
            setChipRoot((current) =>
              // Pressing the active chip again returns the dock to the word the strip is
              // describing, rather than being a no-op. The strip stays either way — it is the
              // context the chip sits in, not a result.
              current?.root === root ? null : { entryId: selected.entryId, root },
            )
          }
        />
      )}

      {dockError && (
        <span className="text-meta text-[color:var(--ink-3)]">{dockError}</span>
      )}

      <ResultList
        lookup={lookup}
        mineSource={mineSource}
        isPhone={isPhone}
        // Read is where the two kinds interleave, so this is where the gutter earns its 26px.
        showKind
        onOpenGrammar={(entry) => onOpenGrammar(entry.id)}
        empty={
          <p className="text-secondary text-ink-3">
            {selectedToken
              ? "辞書に該当する項目がありません。"
              : isPhone
                ? "語をタップすると辞書を引きます。"
                : "語にポインタを合わせると辞書を引きます。"}
          </p>
        }
        className={[
          "flex min-h-0 flex-1 flex-col gap-2",
          // The sheet already scrolls; a second scroller inside it traps the drag.
          isPhone ? "" : "overflow-auto pb-5",
        ].join(" ")}
      />
    </div>
  );

  /**
   * The breadcrumb: `サクラノ詩 · 第三章`.
   *
   * **The folder is not in the tab strip** — it lives here, which is also what makes it the way
   * into the library. The strip is 34px and holds several tabs; repeating a work's name on each of
   * them would leave no room for the names that differ.
   */
  const currentFolder =
    log.folders.find((folder) => folder.id === log.activeLog?.folderId)?.name ?? null;
  const currentLabel = log.activeLogId ? logLabel(log.logs, log.activeLogId) : "";
  const breadcrumb = [currentFolder, currentLabel].filter(Boolean).join(" · ");

  /**
   * The dock's segmented control, and Ask beside Lookup (日本-325, frame `1b`).
   *
   * **The dock is single-tenant, so hover-lookup is suspended while Ask holds it.** Lookup's
   * results are driven by hovering a word in the sentence, and with Ask in the pane there is
   * nowhere for them to render; pointing at a sentence becomes the gutter's `?` and `Ctrl+/`
   * instead. Switching back on hover was considered and rejected — it would eat a half-typed
   * question, which is the one thing a composer must never do.
   *
   * The control is not drawn at all on a deployment with no key: the segment and the ability to use
   * it disappear together, rather than offering one that always fails.
   */
  const askDock = (
    <AskPanel
      configured={ask.available}
      model={ask.model}
      // **Not "in context".** The envelope sends `priorDepth` sentences (5 by default), not the
      // whole log, so a count of every sentence followed by those two words overstated what the
      // model can see by two orders of magnitude on a long log. The count itself is true — it is
      // the log's length — and the context settings are one click away in the ⚙ panel.
      scope={
        currentFolder ? `${currentFolder} · ${entries.length}文` : `${entries.length}文`
      }
      rows={ask.rows}
      busy={ask.busy}
      error={ask.error}
      droppedTurns={ask.droppedTurns}
      chips={ask.chips}
      draft={ask.draft}
      ordinalOf={(sentenceId) => {
        const at = entries.findIndex((entry) => entry.id === sentenceId);
        return at === -1 ? undefined : at + 1;
      }}
      onDraft={ask.setDraft}
      onRemoveChip={ask.unpoint}
      onSend={() => void ask.send()}
      onStop={ask.stop}
      onClear={() => void ask.clear()}
      onDeleteLast={() => void ask.deleteLast()}
      estimate={ask.estimate}
      signalFor={(term) => carded.get(term) ?? { state: "unknown" }}
      // Mining from an Ask answer inherits the sentence and both image slots from the LAST chip
      // attached, falling back to the focused row — hence the source built here rather than
      // `mineSource`, which speaks for the DOCK's selection. Silently inheriting the wrong images
      // is the failure you cannot see.
      onAddEntry={(definition) => {
        const chip = ask.chips[ask.chips.length - 1];
        const source = chip
          ? (entries.find((entry) => entry.id === chip.sentenceId) ?? focusedEntry)
          : focusedEntry;
        void lookup.add(definition, -1, source
          ? {
              text: source.text,
              image: source.image ?? "",
              extraImage: source.extraImage ?? "",
              onMined: (noteId) => log.noteMined(source.id, noteId),
            }
          : null);
      }}
      // A 文法 card the agent drew opens in 文法, exactly as the dock's own does (日本-519).
      onOpenGrammar={onOpenGrammar}
      isPhone={isPhone}
    />
  );

  const segmented = ask.available ? (
    <div
      className={
        isPhone
          ? "flex shrink-0 gap-[2px] rounded-[var(--r-2)] bg-sunken p-[2px]"
          : "mx-[26px] mt-[13px] flex shrink-0 gap-[2px] self-start rounded-[var(--r-2)] bg-sunken p-[2px]"
      }
      role="tablist"
      aria-label="ドックの表示"
    >
      {(["lookup", "ask"] as const).map((value) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={ask.mode === value}
          onClick={() => ask.setMode(value)}
          className={[
            "rounded-[var(--r-2)] px-[10px] py-[4px] text-[14px]/[1] font-semibold",
            ask.mode === value ? "bg-panel text-ink shadow-[var(--sh-frame)]" : "text-ink-3",
          ].join(" ")}
        >
          {value === "lookup" ? "辞書" : "質問"}
        </button>
      ))}
    </div>
  ) : null;

  const dockPane =
    pickerOpen || !segmented ? (
      dock
    ) : (
      <div className="flex h-full min-h-0 flex-col">
        {segmented}
        <div className="min-h-0 flex-1">{ask.mode === "ask" ? askDock : dock}</div>
      </div>
    );


  const strip = (
    <TabStrip
      logs={log.logs}
      openLogIds={log.openLogIds}
      activeLogId={log.activeLogId}
      onSelect={log.setActiveLog}
      onClose={log.closeTab}
      onMoveTab={log.moveTab}
      // **An unsaved log is DISCARDED, not closed** (Alex, 5 Aug 2026): it is never filed, so a
      // closed one would sit in `Unfiled` for ever. The confirm lives here rather than in the strip
      // because whether to ask depends on what is in the log, which is not the strip's business.
      //
      // Silent when empty — that is the scratchpad you clear twice a day, and the rule about Clear
      // not asking applies for the same reason. Asked when it holds sentences, because unlike Clear
      // this cannot be undone and the count is the thing you would want to have known.
      onDiscard={(logId) => {
        const entry = log.logs.find((candidate) => candidate.id === logId);
        const held = entry?.sentenceCount ?? 0;
        if (held === 0) {
          void log.removeLog(logId);
          return;
        }
        void dialogs
          .confirm({
            title: "この未保存のログを破棄しますか？",
            body: "残すなら先に名前を付けてください。",
            detail: `${held}文`,
            confirmLabel: "破棄",
            tone: "danger",
          })
          .then((ok) => {
            if (ok) void log.removeLog(logId);
          });
      }}
      onRename={log.renameLog}
      folders={log.folders}
      onFileLog={log.fileLog}
      onNew={() => void log.newLog()}
      onOpenLibrary={() => onOpenLibrary()}
      onToggleFind={() => (find.open ? find.close() : find.show())}
      findOpen={find.open}
      // Dragging a row onto a tab is the same move the `⋯` menu offers, and the design says so.
      onDropSentence={(logId, sentenceId) => log.moveSentence(sentenceId, logId)}
    />
  );

  const topBar = (
    <>
      <TopBar
        title="読書"
        subtitle={breadcrumb || `${entries.length}文`}
      >
        {/* Absent rather than zero while the hub is unreachable: a confident `0 added` after a
            morning's mining is a worse answer than no answer. `App` renders the same payload the
            opposite way for the rail badge, and both are right — see `useDeckCounts`.

            One gate for both counters, where there used to be two and a defensive separator
            between them. That separator guarded against the two ever arriving apart, which was
            true by discipline while they were separate state; holding the whole payload makes it
            true by construction, so the rule can only ever have something to its left. */}
        {counts !== null && (
          <>
            <span className="font-mono text-secondary font-semibold text-ink">
              {counts.addedToday}
            </span>
            <span className="text-meta text-ink-3">追加済</span>
            <TopBarDivider />
            {/* The backlog you are adding to — the number that says whether to keep mining today.
                The design pairs it with a `· 23d` runway, which needs the pace maths from Stats. */}
            <span className="font-mono text-secondary font-semibold text-[color:var(--accent-text)]">
              {counts.new}
            </span>
            <span className="text-meta text-ink-3">未知</span>
          </>
        )}

      </TopBar>
    </>
  );

  /**
   * The phone's chrome — the 44px bar, and **no tab strip at all.**
   *
   * That is what the Mobile canvas's 読書 frame draws, and it is a real structural decision rather
   * than a re-flow: the strip's job is switching between several open logs, and the frame answers
   * it with `冊` into the library instead. At 390px the strip is a 34px band holding at most two
   * legible tabs plus `N more ›`, which is a jump-list into a list — the library, one tap away and
   * showing every log with its folder and counts.
   *
   * Nothing is lost from the *model*: `openLogIds` is untouched, opening a log from the library
   * still focuses its existing tab, and a desktop opened on the same account still has the strip.
   * What the phone gives up is having several logs open *visibly at once*, which is the half of the
   * strip a thumb could not use anyway.
   *
   * The breadcrumb rides here rather than in the strip, which is where the folder already lived —
   * `サクラノ詩 · 第三章` is the same string the desktop bar carries.
   */
  const phoneTopBar = (
    <TopBar
      title="読書"
      destinationId="read"
      subtitle={breadcrumb || undefined}
      phoneActions={
        <>
          {/* One counter, not two. The desktop bar's pair answers *how much have I done* and *how
              much is waiting*; only the second decides whether to keep mining, and it is the one
              the frame draws. */}
          {counts !== null && (
            <span className="mr-[2px] flex items-center gap-[4px] font-mono text-meta font-semibold text-[color:var(--accent-text)]">
              {counts.new}
              <span className="font-sans text-ink-3">未知</span>
            </span>
          )}
          <TopBarAction
            label="ログ内を検索"
            pressed={find.open}
            onClick={() => (find.open ? find.close() : find.show())}
          >
            <span className="font-jp">検</span>
          </TopBarAction>
          <TopBarAction label="書架" onClick={() => onOpenLibrary()}>
            <span className="font-jp">冊</span>
          </TopBarAction>
        </>
      }
    />
  );

  /**
   * The library, as a SUB-VIEW of Read rather than a rail destination.
   *
   * Returned early, above the phone branch, because it replaces the whole surface below the rail on
   * both. Opening a log from here **focuses its existing tab** if it has one — `logTabs.openLog` is
   * where that rule lives, and it is the one bug this feature would otherwise ship with.
   */
  if (libraryOpen) {
    return (
      <Library
        folders={log.folders}
        logs={log.logs}
        activeLogId={log.activeLogId}
        backLabel={currentLabel || "読書"}
        isPhone={isPhone}
        onBack={onCloseLibrary}
        onOpen={(logId) => {
          // Arrived here from `Move to log ▸ Choose from library…`, so opening a row means "put
          // the sentence there" rather than "read that log". It still opens the tab, because the
          // next thing you want is to see where it went.
          if (movingSentence) {
            log.moveSentence(movingSentence, logId);
          }
          log.openLog(logId);
        }}
        onNewLog={() => void log.newLog()}
        onNewFolder={(name) => void log.createFolder(name)}
        onRenameLog={log.renameLog}
        onFileLog={log.fileLog}
        onDeleteLog={(logId) => void log.removeLog(logId)}
        onClearLog={(logId) => void log.clearLog(logId)}
        onReorderLogs={log.reorderLogs}
        onReorderFolders={log.reorderFolders}
        onRenameFolder={log.renameFolder}
        onDeleteFolder={(folderId) => void log.removeFolder(folderId)}
      />
    );
  }

  const findBand = (
    <FindBand
      query={find.query}
      opened={find.opened}
      count={find.hits.length}
      current={find.current}
      onQuery={find.setQuery}
      onStep={find.step}
      onClose={find.close}
      isPhone={isPhone}
    />
  );

  if (isPhone) {
    return (
      <>
        {phoneTopBar}
        {/* No tray agent on a phone — no hotkey, no capture, no deep link. Text arrives by paste.
            While find is open the band takes this row's place (canvas frame C): the two are never
            wanted at once, and a phone cannot spare the height for both. */}
        {find.open ? findBand : <ComposeRow
          onSubmit={(text) => void log.append(text)}
          onRadicals={() => (pickerOpen ? setPickerOpen(false) : openPicker())}
          radicalsOpen={pickerOpen}
          onPhoto={setPhotoFile}
          photoOpen={photoFile !== null}
        />}
        {photoFile && (
          <Suspense fallback={null}>
            <PhotoCapture
              file={photoFile}
              destination={breadcrumb || "新しいログ"}
              onAdd={async (text, photo) => {
                await log.append(text, undefined, undefined, photo);
              }}
              onClose={() => setPhotoFile(null)}
            />
          </Suspense>
        )}
        <BottomSheet
          surface="read"
          main={logRegion}
          mainRef={scrollerRef}
          sheet={dockPane}
          footer={footer}
          // A lookup arriving in a swiped-away sheet would be invisible; this lifts it back to peek.
          openOn={pickerOpen ? "picker" : (selectedToken?.surface ?? null)}
        />
      </>
    );
  }

  return (
    <>
      {topBar}
      {strip}
      {find.open && findBand}
      <SplitPane
        surface="read"
        main={logRegion}
        mainRef={scrollerRef}
        dock={dockPane}
        defaultDockSize={560}
        // The palette lays out flat, with no scrolling — so opening the picker drags the split up
        // to the height the design gives it, and closing drops back to the reading ratio.
        minDockSize={pickerOpen ? PICKER_DOCK_SIZE : undefined}
      />
    </>
  );
}
